import { useCallback, useEffect, useLayoutEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { usePickupBag } from '@/hooks/usePickupBag';
import PickupDrawer from '@/pages/product/components/PickupDrawer';

const links = [
  { href: '/collection', label: 'Collection' },
  { href: '/story', label: 'Our Story' },
  { href: '/visit', label: 'Visit' },
];

/** Light editorial header for inner pages (ivory ground, soft-black type). */
export default function SiteNav() {
  const [open, setOpen] = useState(false);
  const [dark, setDark] = useState(false);
  // Colour transitions only for scroll-driven changes, never on the first frame of a route.
  const [settled, setSettled] = useState(false);
  const { pathname } = useLocation();
  const { totalQuantity, bagOpen, setBagOpen } = usePickupBag();
  const closeBag = useCallback(() => setBagOpen(false), [setBagOpen]);
  useEffect(() => { setBagOpen(false); }, [pathname, setBagOpen]);

  // First frame of a route: resolve the theme synchronously before paint (and before the
  // view-transition snapshot). Pages enter at the top, so measure in document coordinates.
  useLayoutEffect(() => {
    setSettled(false);
    const el = document.querySelector('[data-nav-dark]');
    const navH = window.innerWidth >= 768 ? 80 : 64;
    setDark(!!el && el.getBoundingClientRect().bottom + window.scrollY > navH);
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => setSettled(true)));
    return () => cancelAnimationFrame(raf);
  }, [pathname]);

  // Dark while a [data-nav-dark] opening section sits under the header; ivory once it scrolls past.
  useEffect(() => {
    const update = () => {
      const el = document.querySelector('[data-nav-dark]');
      const navH = window.innerWidth >= 768 ? 80 : 64;
      setDark(!!el && el.getBoundingClientRect().bottom > navH);
    };
    const raf = requestAnimationFrame(update);
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, [pathname]);

  const onDark = dark && !open;
  const linkCls = ({ isActive }: { isActive: boolean }) => `fold-label whitespace-nowrap cursor-pointer transition-opacity ${isActive ? 'opacity-100' : 'opacity-55 hover:opacity-100'}`;

  return (
    <header className={`fixed inset-x-0 top-0 z-50 border-b ${settled ? 'transition-colors duration-300' : ''} ${onDark ? 'bg-[#171515] text-[#F5F1EB] border-[rgba(245,241,235,0.1)]' : 'bg-[#F5F1EB] text-[#171515] border-[rgba(23,21,21,0.1)]'}`}>
      <nav className="fold-shell flex h-16 md:h-20 items-center justify-between" aria-label="Main">
        <Link to="/" className="fold-serif text-[26px] md:text-[28px] leading-none tracking-[-0.03em] cursor-pointer md:w-[180px]" aria-label="FOLD. home">
          FOLD.
        </Link>
        <ul className="hidden md:flex items-center gap-12">
          {links.map((l) => (
            <li key={l.href}>
              <NavLink to={l.href} className={linkCls}>{l.label}</NavLink>
            </li>
          ))}
        </ul>
        <div className="flex items-center md:w-[180px] justify-end gap-1 md:gap-5">
        <button type="button" onClick={() => setBagOpen(true)} aria-label={`Open pickup bag, ${totalQuantity} pieces`} className="fold-label whitespace-nowrap cursor-pointer h-11 px-2 md:px-0 flex items-center gap-1.5 opacity-80 hover:opacity-100 transition-opacity">
          Bag <span className="tabular-nums">({totalQuantity})</span>
        </button>
        <Link to="/visit#pickup" className="hidden md:inline-flex justify-end fold-label whitespace-nowrap cursor-pointer group">
          <span className={`border px-4 py-2.5 transition-colors ${onDark ? 'border-[rgba(245,241,235,0.35)] group-hover:bg-[#F5F1EB] group-hover:text-[#171515]' : 'border-[rgba(23,21,21,0.3)] group-hover:bg-[#171515] group-hover:text-[#F5F1EB]'}`}>Pickup request</span>
        </Link>
        <button
          type="button"
          className="md:hidden w-11 h-11 flex items-center justify-center cursor-pointer whitespace-nowrap"
          aria-expanded={open}
          aria-controls="fold-site-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((v) => !v)}
        >
          <i className={`${open ? 'ri-close-line' : 'ri-menu-line'} text-xl`}></i>
        </button>
        </div>
      </nav>
      {open && (
        <div id="fold-site-menu" className="md:hidden fold-shell pb-8">
          <ul className="flex flex-col border-t border-[rgba(23,21,21,0.12)]">
            {links.map((l) => (
              <li key={l.href} className="border-b border-[rgba(23,21,21,0.12)]">
                <Link to={l.href} onClick={() => setOpen(false)} className="fold-serif block py-5 text-3xl cursor-pointer">{l.label}</Link>
              </li>
            ))}
          </ul>
        </div>
      )}
      <PickupDrawer open={bagOpen} onClose={closeBag} />
    </header>
  );
}